import { useCallback, useEffect, useRef, useState } from "react";
import { LayoutComponent } from "../LayoutComponent";
import { useSelector } from "react-redux";
import {
  ArrowClockwise,
  ArrowLeft,
  ArrowRight,
  Plus,
  Trash,
} from "react-bootstrap-icons";
import {
  AlertIconFill,
  ArrowForwardIcon,
  MoreIcon,
  ReplyAllIcon,
  ReplyIcon,
  SearchIcon,
  TrashIcon,
} from "../../Icons/IconsLib";
import FilterIcon from "@spectrum-icons/workflow/Filter";
import {
  Flex,
  Content,
  ProgressCircle,
  Button,
  View,
  Link,
  ListBox,
  IllustratedMessage,
  Heading,
  Item,
  ActionButton,
  DialogTrigger,
  Dialog,
  MenuTrigger,
  Menu,
  TextField,
  DatePicker,
  Text,
  Checkbox,
  Divider,
  Form,
  Footer,
  Provider,
} from "@adobe/react-spectrum";
import { ToastQueue } from "@react-spectrum/toast";
import NotFound from "@spectrum-icons/illustrations/NotFound";
import { RootState } from "../../../../state/store";
import moment from "moment";
import { useAsyncList } from "react-stately";
import { useDependency } from "../../../../contexts/DependencyProvider";
import { useViewInfo } from "../../../../hooks/UseViewInfo";
import { useTranslation } from "react-i18next";
import CardComponent from "./component/CardComponent";
import CardComponentLarge from "./component/CardComponentLarge";
import { FPAData } from "../../../../infra/protected/FPA/FPAData";
import {
  getSenderEmail,
  getSenderName,
  truncateText,
} from "../../../../utils/helperUtil";
import MessageAttachment from "./component/MessageAttachmentComponent";
import {
  invokeNewMessageCreating,
  invokeReplyMessage,
} from "../../../../utils/NavigationUtils";
import {
  AtollonEvent,
  subscribe,
  unsubscribe,
} from "../../../../infra/events/Events";
import { useMessageOperations } from "../../../../hooks/useMessageOperations";
import "./message_list_component.css";
import styles from "./message_list_component.module.css";

/**
 * Helper function to extract recipient value from various data formats
 * Handles: arrays, strings, and objects with CONTENT property
 */
const getRecipientValue = (data: any): string | null => {
  if (!data) return null;

  if (Array.isArray(data) && data.length > 0) {
    return data.join(", ");
  }
  if (typeof data === "string" && data.trim()) {
    return data.trim();
  }
  if (data.CONTENT && typeof data.CONTENT === "string" && data.CONTENT.trim()) {
    return data.CONTENT.trim();
  }
  return null;
};

/**
 * Component to render the "To" and "Cc" recipient fields
 * Only renders fields that have valid data
 */
const RecipientToField = ({ recipient }: { recipient: any }) => {
  const toValue = getRecipientValue(recipient?.TO);
  const ccValue = getRecipientValue(recipient?.CC);

  // Don't render anything if neither To nor Cc has data
  if (!toValue && !ccValue) return null;

  return (
    <Flex
      direction="row"
      justifyContent="start"
      alignItems="center"
      width="100%"
      gap="size-200"
      UNSAFE_className={styles.email_to}
    >
      {toValue && (
        <View>
          <span title={toValue} style={{ color: "#728289" }}>
            To
          </span>
          &nbsp;{truncateText(toValue, 150)}
        </View>
      )}
      {ccValue && (
        <View>
          <span title={ccValue} style={{ color: "#728289" }}>
            Cc
          </span>
          &nbsp;{truncateText(ccValue, 150)}
        </View>
      )}
    </Flex>
  );
};

interface MessageListProps {
  readonly selectedItem?: FPAData;
}
export interface Character {
  id: string;
}

function MessageList({ selectedItem }: MessageListProps) {
  const { t } = useTranslation();
  const componentRef = useRef<HTMLDivElement>(null);
  const { store, messageService, mainService } = useDependency();
  const selectedApp = useSelector(
    (state: RootState) => state.userWebLayout.selectedAppId,
  );
  const { isMobile } = useViewInfo();

  useEffect(() => {
    // Check if there's a pending reload from a message that was sent while component was unmounted
    const needsReload = sessionStorage.getItem("messageListNeedsReload");
    const reloadContext = sessionStorage.getItem("messageListReloadContext");

    if (needsReload === "true") {
      // Clear the flag
      sessionStorage.removeItem("messageListNeedsReload");
      sessionStorage.removeItem("messageListReloadContext");

      // Check if this is the right context
      if (!reloadContext || reloadContext === String(selectedItem?.id)) {
        setReloadTrigger((prev) => prev + 1);
      }
    }
  }, [selectedItem?.id]);

  const [rowsData, setRowsData] = useState<any>([]);
  const [messageContent, setMessageContent] = useState<any>("");
  const [selectedKeys, setSelectedKeys] = useState<Set<any>>();
  const [selectedDataRow, setSelectedDataRow] = useState<any>({});
  const [navigationIndex, setNavigationIndex] = useState<number>(0);
  const [showLoader, setShowLoader] = useState<boolean>(true);
  const [showLoaderDetail, setShowLoaderDetail] = useState(true);
  const [showDetail, setShowDetail] = useState<boolean>(false);
  const [stopLoadData, setStopLoadData] = useState<boolean>(false);
  const [totalMessageListRec, setTotalMessageListRec] = useState<number>(0);
  const [emailAttachments, setEmailAttachments] = useState<any>([]);
  const [reloadTrigger, setReloadTrigger] = useState<number>(0); // Add reload trigger

  // Refs for useMessageOperations hook
  const filterParamRef = useRef({
    createdFrom: "",
    createdTo: "",
    sender: "",
    subject: "",
    newOnly: false,
    starred: false,
  });
  const offSetLimitRef = useRef<number>(0);
  const searchedTextRef = useRef<string>("");

  // Create refs object for useMessageOperations
  const refs = {
    filterParamRef,
    offSetLimitRef,
    searchedTextRef,
  };

  // Use the shared message operations hook
  const {
    getMessageListCount,
    loadMessages,
    getEmailContent,
    deleteMessage: deleteMessageOp,
    PAGE_SIZE,
  } = useMessageOperations({
    store,
    messageService,
    mainService,
    refs,
    treeHandle: selectedItem?.id, // Use treeHandle mode for MessageListComponent
  });

  let iconStyle = {
    paddingTop: "5px",
    paddingLeft: "5px",
    color: "#B0BEC5",
    WebkitTextStroke: ".5px",
  };

  // useEffect(() => {
  //   (async () => {
  //     setShowLoaderDetail(true);
  //     setSearchedValue('');
  //   })();
  // }, [selectedApp]);

  const handleNewMessageCreated = useCallback((event: AtollonEvent) => {
    // Reset all state
    setStopLoadData(false);
    setShowLoader(true);
    setShowLoaderDetail(true);
    setSelectedKeys(new Set());
    setSelectedDataRow(null);
    setMessageContent("");
    setNavigationIndex(0);
    setTotalMessageListRec(0);
    setRowsData([]);
    offSetLimitRef.current = 0;

    // Trigger reload by incrementing the trigger
    setReloadTrigger((prev) => prev + 1);
  }, []);

  // Effect to reload when trigger changes
  useEffect(() => {
    if (reloadTrigger > 0) {
      listMessageList.reload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadTrigger]);

  useEffect(() => {
    listMessageList.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem]);

  useEffect(() => {
    subscribe(AtollonEvent.NEW_MESSAGE_CREATED, handleNewMessageCreated);
    return () => {
      unsubscribe(AtollonEvent.NEW_MESSAGE_CREATED, handleNewMessageCreated);
    };
  }, [handleNewMessageCreated]);

  // Wrapper for getEmailContent that also handles attachments for this component
  const getEmailContentAndAttachments = async (selectedRow: any) => {
    const messageContent = await getEmailContent(selectedRow);

    let emailAtts = messageContent.MESSAGE?.ATTACHMENT || [];
    if (!Array.isArray(emailAtts)) {
      emailAtts = [emailAtts];
    }

    const processedAttachments = emailAtts.map((file: any) => ({
      id: file.id,
      fileName: file.name,
      fileSize: file.size,
    }));

    setEmailAttachments(processedAttachments);
    return messageContent;
  };

  let listMessageList = useAsyncList<Character>({
    async load({ cursor, filterText }) {
      if (selectedApp === "") {
        setShowLoader(false);
        setShowLoaderDetail(false);
        return { items: rowsData, cursor: undefined };
      }
      if (cursor && Number(cursor) * PAGE_SIZE >= totalMessageListRec) {
        setShowLoader(false);
        setShowLoaderDetail(false);
        return { items: rowsData, cursor: undefined };
      }

      try {
        let totalRecords = await getMessageListCount(filterText || "");
        totalRecords = totalRecords ? Number(totalRecords) : 0;
        setTotalMessageListRec(totalRecords);

        let currentPage = cursor ? parseInt(cursor) : 0;
        let offSet = PAGE_SIZE * currentPage;
        offSetLimitRef.current = offSet;

        if (stopLoadData) {
          setShowLoader(false);
          setShowLoaderDetail(false);
          return { items: rowsData, cursor: undefined };
        }

        let listMessages = await loadMessages(filterText || "");
        setRowsData((prev: any) =>
          currentPage === 0 ? listMessages : [...prev, ...listMessages],
        );

        if (currentPage === 0 && listMessages.length > 0) {
          setSelectedKeys(new Set([listMessages[0].id]));
          setSelectedDataRow(listMessages[0]);
          let messageContent = await getEmailContentAndAttachments(
            listMessages[0],
          );
          setMessageContent(messageContent);
        }

        return {
          items: listMessages,
          cursor: String(offSet >= totalRecords ? undefined : currentPage + 1),
        };
      } catch (error) {
        console.error("Error loading messages:", error);
        return { items: [], cursor: undefined };
      } finally {
        setShowLoader(false);
        setShowLoaderDetail(false);
      }
    },
  });

  const resetMessageListState = (filterText: string) => {
    setStopLoadData(false);
    setShowLoader(true);
    setShowLoaderDetail(true);
    setSelectedKeys(new Set());
    setSelectedDataRow(null);
    setMessageContent("");
    setNavigationIndex(0);
    setTotalMessageListRec(0);
    setRowsData([]);
    offSetLimitRef.current = 0;
    listMessageList.setFilterText(filterText);
  };

  const searchMessageList = useCallback(async () => {
    filterParamRef.current = {
      createdFrom: "",
      createdTo: "",
      sender: "",
      subject: "",
      newOnly: false,
      starred: false,
    };
    resetMessageListState(searchedTextRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reloadMessageList = useCallback(() => {
    filterParamRef.current = {
      createdFrom: "",
      createdTo: "",
      sender: "",
      subject: "",
      newOnly: false,
      starred: false,
    };
    resetMessageListState("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterMessageList = useCallback(() => {
    resetMessageListState("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleListRowClick = async (rowId: any): Promise<void> => {
    const currentKey = rowId?.currentKey;
    if (!currentKey || selectedKeys?.has(currentKey)) return;

    setShowLoaderDetail(true);
    setSelectedKeys(rowId);

    const recIndex = rowsData.findIndex((item: any) => item.id === currentKey);
    setNavigationIndex(recIndex);

    const selectedRow = rowsData.find((item: any) => item.id === currentKey);
    console.log("selectedRow", selectedRow);
    if (!selectedRow) {
      setSelectedDataRow(null);
      setSelectedKeys(new Set());
      setShowLoaderDetail(false);
      return;
    }

    setSelectedDataRow(selectedRow);

    const messageContent = await getEmailContentAndAttachments(selectedRow);
    setMessageContent(messageContent);
    setShowLoaderDetail(false);
  };

  const handleListRowLargeClick = async (rowId: any): Promise<void> => {
    const currentKey = rowId?.currentKey || selectedDataRow?.id;
    if (!currentKey) return;

    setShowLoaderDetail(true);
    setSelectedKeys(rowId ?? selectedKeys);

    const recIndex = rowsData.findIndex((item: any) => item.id === currentKey);
    setNavigationIndex(recIndex);

    const selectedRow = rowsData.find((item: any) => item.id === currentKey);
    if (!selectedRow) {
      setSelectedDataRow(null);
      setSelectedKeys(new Set());
      setShowLoaderDetail(false);
      return;
    }

    setSelectedDataRow(selectedRow);

    const messageContent = await getEmailContentAndAttachments(selectedRow);
    setMessageContent(messageContent);
    setShowLoaderDetail(false);
    setShowDetail(true);
  };

  const handleReplyClick = async (reply_type: string) => {
    selectedDataRow.RECIPIENT.TO.CONTENT = getSenderEmail(
      selectedDataRow.mailFrom,
    );
    invokeReplyMessage(
      selectedItem?.id,
      reply_type,
      selectedDataRow,
      emailAttachments,
    );
  };

  const Loader = () => (
    <Flex width="100%" justifyContent="center" marginTop={10}>
      <ProgressCircle aria-label="Loading…" isIndeterminate />
    </Flex>
  );

  const NoResultsMessage = () => (
    <Flex
      direction="row"
      alignContent="center"
      justifyContent="center"
      marginTop={"30px"}
    >
      <IllustratedMessage>
        <NotFound />
        <Heading>{t("no_results", { ns: "layout_components" })}</Heading>
      </IllustratedMessage>
    </Flex>
  );

  const nextNavigation = async () => {
    setShowLoaderDetail(true);
    setSelectedKeys(new Set([rowsData[navigationIndex + 1].id]));
    let selectedRow = rowsData.find(
      (item: any) => item.id === rowsData[navigationIndex + 1].id,
    );
    if (!selectedRow) {
      return;
    }
    setSelectedDataRow(selectedRow);
    let messageContent = await getEmailContentAndAttachments(selectedRow);
    setMessageContent(messageContent);
    setShowLoaderDetail(false);
    setNavigationIndex((prev) => prev + 1);
  };

  const previousNavigation = async () => {
    setShowLoaderDetail(true);
    setSelectedKeys(new Set([rowsData[navigationIndex - 1].id]));

    let selectedRow = rowsData.find(
      (item: any) => item.id === rowsData[navigationIndex - 1].id,
    );
    if (!selectedRow) {
      return;
    }
    setSelectedDataRow(selectedRow);
    let messageContent = await getEmailContentAndAttachments(selectedRow);
    setMessageContent(messageContent);
    setShowLoaderDetail(false);
    setNavigationIndex((prev) => prev - 1);
  };

  const handleDeleteMessage = async (messageId: string) => {
    const success = await deleteMessageOp(messageId);

    if (success) {
      listMessageList.remove(messageId);

      let nextIndex = rowsData.findIndex((item: any) => item.id === messageId);
      let nextMessageObj =
        rowsData.length >= nextIndex + 1 ? rowsData[nextIndex + 1] : null;

      setRowsData((prev: any) =>
        prev.filter((item: any) => item.id !== messageId),
      );

      if (nextMessageObj) {
        setSelectedKeys(new Set([nextMessageObj.id]));
        setSelectedDataRow(nextMessageObj);
        let messageContent =
          await getEmailContentAndAttachments(nextMessageObj);
        setMessageContent(messageContent);
      }

      ToastQueue.info(
        t("message_deleted_successfully", { ns: "layout_components" }),
        { timeout: 50 },
      );
    } else {
      ToastQueue.negative(
        t("error_deleting_message", { ns: "layout_components" }),
        { timeout: 50 },
      );
    }
  };

  const MessageDialog = ({ close }: { close: () => void }) => (
    <Dialog size="S">
      <Flex UNSAFE_className={styles.delete_pop_up_parent}>
        <Content UNSAFE_className={styles.delete_pop_up_header}>
          {t("remove_message", { ns: "layout_components" })}
        </Content>
        <View UNSAFE_className={styles.delete_pop_up_line}></View>
        <View UNSAFE_className={styles.delete_pop_up_warning_text}>
          {t("are_you_sure_you_want_to_remove_message", {
            ns: "layout_components",
          })}
        </View>
        <View UNSAFE_className={styles.buttons_parent}>
          <View UNSAFE_className={styles.button_cancel}>
            <Link
              UNSAFE_className={styles.delete_pop_up_cancel_text}
              isQuiet
              onPress={() => {
                close();
              }}
            >
              {t("no_keep_it", { ns: "layout_components" })}
            </Link>
          </View>
          <View UNSAFE_className={styles.button_confirm}>
            <div className={styles.trash_icon}>
              <TrashIcon size={32} />
            </div>
            <Link
              UNSAFE_className={styles.delete_pop_up_confirm_text}
              isQuiet
              onPress={() => {
                handleDeleteMessage(selectedDataRow.id);
                close();
              }}
            >
              {t("yes_remove", { ns: "layout_components" })}
            </Link>
          </View>
        </View>
        <View UNSAFE_className={styles.ellipse_div}>
          <View UNSAFE_className={styles.delete_alert_icon}>
            <AlertIconFill size={72} />
          </View>
        </View>
      </Flex>
    </Dialog>
  );

  const RemoveMessageButton = () => (
    <DialogTrigger isDismissable type="modal">
      <ActionButton UNSAFE_className={styles.btn_delete}>
        <Trash className={styles.icon_trash} size={24} />
      </ActionButton>
      {(close) => (
        <MessageDialog
          close={() => {
            close();
          }}
        />
      )}
    </DialogTrigger>
  );

  const SearchDialog = ({ close }: { close: () => void }) => (
    <Dialog size="S" UNSAFE_className={styles.dialog_parent}>
      <Heading>
        <Flex alignItems="center" gap="size-100">
          <Text>{t("search", { ns: "layout_components" })}</Text>
        </Flex>
      </Heading>
      <Divider />
      <Content>
        <Form validationBehavior="native">
          <TextField
            autoFocus
            isRequired
            label={t("search", { ns: "layout_components" })}
            maxWidth="100%"
            onChange={(e) => {
              searchedTextRef.current = e;
            }}
            validationState="invalid"
          />
        </Form>
      </Content>
      <Footer>
        <Flex
          direction={"row"}
          width={"100%"}
          alignContent={"center"}
          justifyContent={"center"}
          alignItems={"center"}
          gap={"size-200"}
        >
          <Flex
            direction={"row"}
            width={"120px"}
            height={"40px"}
            flexShrink={0}
            alignItems={"center"}
            justifyContent={"center"}
            UNSAFE_className={styles.search_button_cancel}
          >
            <Link
              UNSAFE_className={styles.search_pop_up_cancel_text}
              isQuiet
              onPress={() => {
                close();
              }}
            >
              {t("cancel", { ns: "layout_components" })}
            </Link>
          </Flex>
          <Flex
            direction={"row"}
            width={"120px"}
            height={"40px"}
            alignItems={"center"}
            justifyContent={"center"}
            UNSAFE_className={styles.search_button_confirm}
          >
            <Link
              UNSAFE_className={styles.search_pop_up_confirm_text}
              isQuiet
              onPress={() => {
                if (searchedTextRef.current === "") {
                  return;
                }
                searchMessageList();
                searchedTextRef.current = "";
                close();
              }}
            >
              {t("search", { ns: "layout_components" })}
            </Link>
          </Flex>
        </Flex>
      </Footer>
    </Dialog>
  );

  const SearchButton = () => (
    <DialogTrigger>
      <ActionButton UNSAFE_className={styles.no_border}>
        <SearchIcon size={24} />
      </ActionButton>
      {(close) => (
        <SearchDialog
          close={() => {
            close();
          }}
        />
      )}
    </DialogTrigger>
  );

  const FilterDialog = ({ close }: { close: () => void }) => (
    <Dialog size="M" UNSAFE_className={styles.dialog_parent}>
      <Heading>
        <Flex alignItems="center" gap="size-100">
          <Text>{t("Filter", { ns: "layout_components" })}</Text>
        </Flex>
      </Heading>
      <Divider />
      <Content>
        <Form>
          <Provider locale="en-US" UNSAFE_style={{ width: "100%" }}>
            <DatePicker
              width={"100%"}
              autoFocus
              id="dtCreatedFrom"
              label="Created from"
              onChange={(e) =>
                (filterParamRef.current.createdFrom = e?.toString() ?? "")
              }
            />
          </Provider>
          <Provider locale="en-US">
            <DatePicker
              width={"100%"}
              label="Created to"
              onChange={(e) =>
                (filterParamRef.current.createdTo = e?.toString() ?? "")
              }
            />
          </Provider>
          <TextField
            label="Sender"
            onChange={(e) => (filterParamRef.current.sender = e)}
          />
          <TextField
            label="Subject"
            onChange={(e) => (filterParamRef.current.subject = e)}
          />
          <Checkbox onChange={(e) => (filterParamRef.current.newOnly = e)}>
            Only new
          </Checkbox>
          <Checkbox onChange={(e) => (filterParamRef.current.starred = e)}>
            Only starred
          </Checkbox>
        </Form>
      </Content>
      <Footer>
        <Flex
          direction={"row"}
          width={"100%"}
          alignContent={"center"}
          justifyContent={"center"}
          alignItems={"center"}
          gap={"size-200"}
        >
          <Flex
            direction={"row"}
            width={"120px"}
            height={"40px"}
            flexShrink={0}
            alignItems={"center"}
            justifyContent={"center"}
            UNSAFE_className={styles.filter_button_cancel}
          >
            <Link
              UNSAFE_className={styles.filter_pop_up_cancel_text}
              isQuiet
              onPress={() => {
                close();
              }}
            >
              {t("cancel", { ns: "layout_components" })}
            </Link>
          </Flex>
          <Flex
            direction={"row"}
            width={"120px"}
            height={"40px"}
            alignItems={"center"}
            justifyContent={"center"}
            UNSAFE_className={styles.filter_button_confirm}
          >
            <Link
              UNSAFE_className={styles.filter_pop_up_confirm_text}
              isQuiet
              onPress={() => {
                filterMessageList();
                close();
              }}
            >
              {t("search", { ns: "layout_components" })}
            </Link>
          </Flex>
        </Flex>
      </Footer>
    </Dialog>
  );

  const FilterButton = () => (
    <DialogTrigger type="modal">
      <ActionButton UNSAFE_className={styles.no_border}>
        <FilterIcon UNSAFE_style={{ fill: "#b0bec5" }} />
      </ActionButton>
      {(close) => (
        <FilterDialog
          close={() => {
            close();
          }}
        />
      )}
    </DialogTrigger>
  );

  const menuAction = (key: string) => {
    if (key === "delete") {
      handleDeleteMessage(selectedDataRow.id);
    }
  };

  const renderMessageList = () => {
    return showLoaderDetail ? (
      <View
        position={"relative"}
        width={"100%"}
        height={"400px"}
        overflow={"hidden"}
        UNSAFE_className={styles.menu_messages_large_parent}
      >
        <Loader />
      </View>
    ) : (
      <View
        position={"relative"}
        width={"100%"}
        height={"100vh"}
        overflow={"hidden"}
        UNSAFE_className={styles.menu_messages_large_parent}
      >
        <View
          position={"absolute"}
          width={"100%"}
          height={"64px"}
          overflow={"hidden"}
        >
          <Flex
            direction={"row"}
            position={"absolute"}
            top={24}
            height={"32px"}
            alignItems={"center"}
            justifyContent={"center"}
            marginStart={12}
            UNSAFE_className={styles.btn_new_email}
          >
            {/* {showNewMessageDialog && <NewMessage styles={styles} store={store} messageService={messageService} dialogVisibility={showNewMessageDialog} />} */}
            <Button
              variant="primary"
              UNSAFE_style={{
                background: "transparent",
                border: "none",
                width: "100%",
                fontSize: "14px",
              }}
              onPress={() => {
                //setShowNewMessageDialog(true);
                invokeNewMessageCreating(
                  selectedItem?.id,
                  "message",
                  undefined,
                  undefined,
                  true,
                );
              }}
            >
              <>
                <Plus size={18} className={styles.btn_new_email_text} />
                <span className={styles.btn_new_email_text}>
                  {t("new_email", { ns: "layout_components" })}
                </span>
              </>
            </Button>
          </Flex>

          <Flex
            position={"absolute"}
            direction={"row"}
            alignItems={"center"}
            justifyContent={"end"}
            gap={18}
            right={10}
            top={25}
          >
            <ArrowClockwise
              className={styles.message_list_header_icon}
              onClick={reloadMessageList}
            />
            {/* <SearchIcon size={24} /> */}
            <SearchButton />
            {/* <FilterIcon UNSAFE_className={styles.message_list_header_icon} /> */}
            <FilterButton />
            <MoreIcon size={24} />
          </Flex>
        </View>
        <View
          position={"absolute"}
          top={"65px"}
          left={"0px"}
          width={"100%"}
          height={"100vh"}
          overflow={"hidden"}
          UNSAFE_style={{
            fontSize: "12px",
            color: "#36474f",
            borderRadius: "16px",
            backgroundColor: "#fff",
          }}
        >
          <Flex
            direction={"column"}
            position={"absolute"}
            top={"0px"}
            left={"0px"}
            width={"100%"}
            alignItems={"start"}
            justifyContent={"start"}
            maxHeight={"100vh"}
          >
            <ListBox
              autoFocus
              id="listMessageCardsMobile"
              selectionMode="single"
              onSelectionChange={handleListRowLargeClick}
              defaultSelectedKeys={selectedKeys}
              selectedKeys={selectedKeys}
              aria-label="MessageListComponentMobile"
              items={listMessageList.items}
              isLoading={listMessageList.isLoading}
              onLoadMore={
                totalMessageListRec > rowsData.length
                  ? listMessageList.loadMore
                  : undefined
              }
              width={"100%"}
              UNSAFE_style={{ padding: "0 0 0 5px" }}
            >
              {(item) => (
                <Item key={item.id}>
                  {isMobile ? (
                    <CardComponent item={item} />
                  ) : (
                    <CardComponentLarge item={item} />
                  )}
                </Item>
              )}
            </ListBox>
          </Flex>
        </View>
      </View>
    );
  };

  const renderMessageListDetailLarge = () => {
    return (
      <View
        width={"100%"}
        position={"relative"}
        height={"100vh"}
        overflow={"hidden"}
        UNSAFE_className={styles.message_list_detail_wrapper_large}
      >
        <Flex
          direction={"column"}
          position={"absolute"}
          top={0}
          left={0}
          width={"100%"}
          height={"98%"}
          alignItems={"start"}
          justifyContent={"start"}
          UNSAFE_className={styles.message_list_detail_parent}
        >
          <Flex
            direction={"column"}
            height={"98%"}
            alignSelf={"stretch"}
            alignItems={"center"}
            justifyContent={"start"}
          >
            <View
              position={"relative"}
              top={0}
              right={0}
              width={"100%"}
              height={"64px"}
              overflow={"hidden"}
              UNSAFE_className={styles.email_content_header_parent_large}
            >
              <Flex
                direction={"row"}
                position={"absolute"}
                top={"24px"}
                left={"10px"}
                justifyContent={"start"}
                gap={"18px"}
                alignItems={"center"}
              >
                <Flex
                  direction={"row"}
                  alignItems={"center"}
                  justifyContent={"center"}
                  gap={"12px"}
                >
                  <View marginStart={"5px"} marginEnd={"3px"}>
                    <ArrowLeft
                      size={24}
                      className={styles.message_list_header_icon}
                      onClick={() => {
                        setShowDetail(false);
                      }}
                    />
                  </View>
                  <Flex
                    direction={"row"}
                    width={"34px"}
                    height={"34px"}
                    flexShrink={0}
                    justifyContent={"center"}
                    UNSAFE_className={styles.action_button}
                  >
                    <ActionButton
                      onPress={() => {
                        handleReplyClick("REPLY");
                      }}
                      UNSAFE_className={styles.action_button}
                      aria-label="Reply"
                    >
                      <ReplyIcon size={24} />
                    </ActionButton>
                  </Flex>
                  <Flex
                    direction={"row"}
                    width={"34px"}
                    height={"34px"}
                    flexShrink={0}
                    justifyContent={"center"}
                    UNSAFE_className={styles.action_button}
                  >
                    <ActionButton
                      onPress={() => {
                        handleReplyClick("REPLYALL");
                      }}
                      UNSAFE_className={styles.action_button}
                      aria-label="Reply all"
                    >
                      <ReplyAllIcon size={24} />
                    </ActionButton>
                  </Flex>
                  <Flex
                    direction={"row"}
                    width={"34px"}
                    height={"34px"}
                    flexShrink={0}
                    justifyContent={"center"}
                    UNSAFE_className={styles.action_button}
                  >
                    <ArrowForwardIcon size={24} />
                  </Flex>
                </Flex>
                <View
                  position={"relative"}
                  width={"1px"}
                  height={"24px"}
                  UNSAFE_className={styles.message_list_detail_header_divider}
                ></View>
                <Flex
                  direction={"row"}
                  height={"32px"}
                  alignItems={"center"}
                  justifyContent={"center"}
                  width={"100px"}
                  UNSAFE_className={styles.btn_comment}
                  UNSAFE_style={{ display: "none" }}
                >
                  <button
                    className={styles.btn_comment_text}
                    style={{
                      background: "transparent",
                      border: "none",
                      width: "100%",
                      fontSize: "14px",
                    }}
                  >
                    {t("comment", { ns: "layout_components" })}
                  </button>
                </Flex>
              </Flex>
              <Flex
                direction={"row"}
                position={"absolute"}
                top={"27px"}
                right={"20px"}
                alignItems={"center"}
                justifyContent={"start"}
                gap={"10px"}
              >
                <RemoveMessageButton />
                <MoreIcon size={24} />
              </Flex>
            </View>
            <Flex
              direction={"column"}
              width={"100%"}
              alignItems={"start"}
              justifyContent={"start"}
              UNSAFE_className={styles.email_content_header_parent_large}
            >
              <Flex
                direction={"row"}
                alignSelf={"stretch"}
                alignItems={"start"}
                justifyContent={"start"}
                gap={"18px"}
                UNSAFE_style={{
                  borderBottom: "1px solid #eceff1",
                  paddingLeft: "13px",
                  paddingBottom: "5px",
                }}
              >
                <View UNSAFE_className={styles.user_img}>
                  <img
                    id="img_renderMessageListDetailLarge"
                    className={styles.avatar_desktop_light}
                    alt=""
                    src="../../../../../../assets/images/avatar--desktop--light@2x.png"
                  />
                </View>
                <Flex
                  direction={"column"}
                  width={"100%"}
                  alignItems={"start"}
                  justifyContent={"start"}
                  gap={"5px"}
                >
                  <Flex
                    direction={"row"}
                    alignSelf={"stretch"}
                    alignItems={"end"}
                    justifyContent={"space-between"}
                  >
                    <Flex
                      direction={"row"}
                      alignItems={"center"}
                      justifyContent={"start"}
                      gap={"8px"}
                    >
                      <View UNSAFE_className={styles.user_full_name}>
                        {getSenderName(selectedDataRow.sender)}
                      </View>
                      <View
                        width={"1px"}
                        position={"relative"}
                        height={"12px"}
                        UNSAFE_className={styles.user_detail_divider}
                      ></View>
                      <View UNSAFE_className={styles.user_email}>
                        {getSenderEmail(selectedDataRow.mailFrom)}
                      </View>
                    </Flex>
                    <View
                      position={"absolute"}
                      right={"25px"}
                      UNSAFE_className={styles.div_date}
                    >
                      {moment(selectedDataRow.date).format("DD.MM.YYYY HH:mm")}
                    </View>
                  </Flex>
                  <Flex
                    direction={"column"}
                    alignSelf={"stretch"}
                    alignItems={"start"}
                    justifyContent={"start"}
                    gap={"8px"}
                    UNSAFE_className={styles.subject_parent}
                  >
                    <View
                      position={"relative"}
                      alignSelf={"stretch"}
                      UNSAFE_className={styles.email_subject}
                    >
                      {selectedDataRow.subject}
                    </View>
                    <RecipientToField recipient={selectedDataRow?.RECIPIENT} />
                    <Flex
                      direction={"row"}
                      alignItems={"center"}
                      justifyContent={"center"}
                      gap={"6px"}
                    >
                      <View UNSAFE_className={styles.address}>
                        {messageContent.MESSAGE?.folderName}
                      </View>
                      {messageContent.MESSAGE?.projectName && (
                        <>
                          <View UNSAFE_className={styles.icon_chevron_right}>
                            {">"}
                          </View>
                          <View UNSAFE_className={styles.address}>
                            {messageContent.MESSAGE?.projectName}
                          </View>
                        </>
                      )}
                      {messageContent.MESSAGE?.activityName && (
                        <>
                          <View UNSAFE_className={styles.icon_chevron_right}>
                            {" "}
                            {">"}{" "}
                          </View>
                          <View UNSAFE_className={styles.address}>
                            {messageContent.MESSAGE?.activityName}
                          </View>
                        </>
                      )}
                    </Flex>
                    {messageContent?.participantsName &&
                      messageContent.type === "12" && (
                        <Flex
                          direction={"row"}
                          alignItems={"center"}
                          justifyContent={"center"}
                          gap={"6px"}
                        >
                          <View UNSAFE_className={styles.address}>
                            Participants: {messageContent.participantsName}
                          </View>
                        </Flex>
                      )}
                    <MessageAttachment
                      store={store}
                      styles={styles}
                      messageContent={messageContent}
                    />
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
            <View
              width={"93%"}
              position={"relative"}
              UNSAFE_className={styles.email_content}
              height={"75%"}
            >
              {/* <div
                dangerouslySetInnerHTML={{
                  __html: oldEmailContentFix(
                    messageContent.MESSAGE?.PREVIEW?.content
                  ),
                }}
              /> */}
              <iframe
                srcDoc={oldEmailContentFix(
                  messageContent.MESSAGE?.PREVIEW?.content,
                )}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                  backgroundColor: "#fff",
                }}
                title="Email Content"
                sandbox="allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox"
              />
            </View>
          </Flex>
        </Flex>
        <Flex
          direction={"column"}
          position={"absolute"}
          bottom={"16px"}
          left={"calc(50% - 128.5px)"}
          alignItems={"center"}
          justifyContent={"end"}
        >
          <Flex
            direction={"row"}
            width={"258px"}
            alignItems={"center"}
            justifyContent={"center"}
            gap={"32px"}
          >
            {navigationIndex > 0 && (
              <Flex
                direction={"row"}
                height={"32px"}
                alignItems={"center"}
                justifyContent={"center"}
                gap={"8px"}
                UNSAFE_className={styles.btn_previous}
              >
                <Button
                  variant="primary"
                  onPress={previousNavigation}
                  UNSAFE_style={{
                    background: "transparent",
                    border: "none",
                    width: "100%",
                  }}
                >
                  <>
                    <ArrowLeft className={styles.icon_navigate} />
                    &nbsp;&nbsp;&nbsp;
                    <View UNSAFE_className={styles.btn_text}>
                      {t("previous", { ns: "layout_components" })}
                    </View>
                  </>
                </Button>
              </Flex>
            )}
            {navigationIndex < rowsData.length - 1 && (
              <Flex
                direction={"row"}
                width={"113px"}
                height={"32px"}
                flexShrink={0}
                alignItems={"center"}
                justifyContent={"center"}
                gap={"8px"}
                UNSAFE_className={styles.btn_next}
              >
                <Button
                  variant="primary"
                  onPress={nextNavigation}
                  UNSAFE_style={{
                    background: "transparent",
                    border: "none",
                    width: "100%",
                  }}
                >
                  <>
                    <View UNSAFE_className={styles.btn_text}>
                      {t("next", { ns: "layout_components" })}
                    </View>
                    &nbsp;&nbsp;&nbsp;
                    <ArrowRight className={styles.icon_navigate} />
                  </>
                </Button>
              </Flex>
            )}
          </Flex>
        </Flex>
      </View>
    );
  };

  const renderMessageListDetailMobile = () => {
    return (
      <View
        width={"100%"}
        position={"relative"}
        height={"100vh"}
        overflow={"hidden"}
        UNSAFE_className={styles.message_list_detail_wrapper_large}
      >
        <Flex
          direction={"column"}
          position={"absolute"}
          top={0}
          left={0}
          width={"100%"}
          height={"98%"}
          alignItems={"start"}
          justifyContent={"start"}
          UNSAFE_className={styles.message_list_detail_parent}
        >
          <Flex
            direction={"column"}
            height={"98%"}
            alignSelf={"stretch"}
            alignItems={"center"}
            justifyContent={"start"}
          >
            <View
              position={"relative"}
              top={0}
              right={0}
              width={"100%"}
              height={"64px"}
              overflow={"hidden"}
              UNSAFE_className={styles.email_content_header_parent_large}
            >
              <Flex
                direction={"row"}
                position={"absolute"}
                top={"25px"}
                left={"10px"}
                justifyContent={"start"}
                gap={"18px"}
                alignItems={"center"}
              >
                <Flex
                  direction={"row"}
                  alignItems={"center"}
                  justifyContent={"center"}
                  gap={"12px"}
                >
                  <View marginStart={"5px"} marginEnd={"3px"}>
                    <ArrowLeft
                      size={24}
                      className={styles.message_list_header_icon}
                      onClick={() => {
                        setShowDetail(false);
                      }}
                    />
                  </View>
                  <Flex
                    direction={"row"}
                    width={"34px"}
                    height={"34px"}
                    flexShrink={0}
                    justifyContent={"center"}
                    UNSAFE_className={styles.action_button}
                  >
                    <ActionButton
                      onPress={() => {
                        handleReplyClick("REPLY");
                      }}
                      UNSAFE_className={styles.action_button}
                      aria-label="Reply"
                    >
                      <ReplyIcon size={24} />
                    </ActionButton>
                  </Flex>
                  <Flex
                    direction={"row"}
                    width={"34px"}
                    height={"34px"}
                    flexShrink={0}
                    justifyContent={"center"}
                    UNSAFE_className={styles.action_button}
                  >
                    <ActionButton
                      onPress={() => {
                        handleReplyClick("REPLYALL");
                      }}
                      UNSAFE_className={styles.action_button}
                      aria-label="Reply all"
                    >
                      <ReplyAllIcon size={24} />
                    </ActionButton>
                  </Flex>
                  <Flex
                    direction={"row"}
                    width={"34px"}
                    height={"34px"}
                    flexShrink={0}
                    justifyContent={"center"}
                    UNSAFE_className={styles.action_button}
                  >
                    <ArrowForwardIcon size={24} />
                  </Flex>
                </Flex>
                <View
                  position={"relative"}
                  width={"1px"}
                  height={"24px"}
                  UNSAFE_className={styles.message_list_detail_header_divider}
                ></View>
                <Flex
                  direction={"row"}
                  height={"32px"}
                  alignItems={"center"}
                  justifyContent={"center"}
                  width={"100px"}
                  UNSAFE_className={styles.btn_comment}
                  UNSAFE_style={{ display: "none" }}
                >
                  <button
                    className={styles.btn_comment_text}
                    style={{
                      background: "transparent",
                      border: "none",
                      width: "100%",
                      fontSize: "14px",
                    }}
                  >
                    {t("comment", { ns: "layout_components" })}
                  </button>
                </Flex>
              </Flex>
              <Flex
                direction={"row"}
                position={"absolute"}
                top={"32px"}
                right={"2px"}
                alignItems={"center"}
                justifyContent={"start"}
              >
                {/* <RemoveMessageButton /> */}
                <MenuTrigger>
                  <Link>
                    <span aria-label="Open context menu" role="button">
                      <i className="bi bi-three-dots fs-7"></i>
                    </span>
                  </Link>
                  <Menu width={"100%"} onAction={(key: any) => menuAction(key)}>
                    <Item key="delete" textValue="delete">
                      <i className="bi bi-trash" style={iconStyle}></i>
                      &nbsp;&nbsp;
                      <span
                        style={{
                          color: "#36474F",
                          paddingLeft: "5px",
                          paddingTop: "4px",
                        }}
                      >
                        {t("remove", { ns: "layout_components" })}
                      </span>
                    </Item>
                  </Menu>
                </MenuTrigger>
              </Flex>
            </View>
            <Flex
              direction={"column"}
              width={"100%"}
              alignItems={"start"}
              justifyContent={"start"}
              UNSAFE_className={styles.email_content_header_parent_large}
            >
              <Flex
                direction={"row"}
                alignSelf={"stretch"}
                alignItems={"start"}
                justifyContent={"start"}
                gap={"18px"}
                UNSAFE_style={{
                  borderBottom: "1px solid #eceff1",
                  paddingLeft: "13px",
                  paddingBottom: "15px",
                }}
              >
                <View UNSAFE_className={styles.user_img}>
                  <img
                    id="img_renderMessageListDetailMobile"
                    className={styles.avatar_desktop_light}
                    alt=""
                    src="../../../../../../assets/images/avatar--desktop--light@2x.png"
                  />
                </View>
                <Flex
                  direction={"column"}
                  width={"100%"}
                  alignItems={"start"}
                  justifyContent={"start"}
                  gap={"5px"}
                >
                  <Flex
                    direction={"row"}
                    alignSelf={"stretch"}
                    alignItems={"end"}
                    justifyContent={"space-between"}
                  >
                    <Flex
                      direction={"row"}
                      alignItems={"center"}
                      justifyContent={"start"}
                      gap={"8px"}
                    >
                      <View UNSAFE_className={styles.user_full_name}>
                        {getSenderName(selectedDataRow.sender)}
                      </View>
                      <View
                        width={"1px"}
                        position={"relative"}
                        height={"12px"}
                        UNSAFE_className={styles.user_detail_divider}
                      ></View>
                      <View UNSAFE_className={styles.user_email}>
                        {getSenderEmail(selectedDataRow.mailFrom)}
                      </View>
                    </Flex>
                  </Flex>
                  <Flex
                    direction={"column"}
                    alignSelf={"stretch"}
                    alignItems={"start"}
                    justifyContent={"start"}
                    gap={"8px"}
                    UNSAFE_className={styles.subject_parent}
                  >
                    <View
                      position={"relative"}
                      alignSelf={"stretch"}
                      UNSAFE_className={styles.email_subject}
                    >
                      {selectedDataRow.subject}
                    </View>
                    <RecipientToField recipient={selectedDataRow?.RECIPIENT} />
                    <Flex
                      direction={"row"}
                      alignItems={"center"}
                      justifyContent={"center"}
                      gap={"6px"}
                    >
                      <View UNSAFE_className={styles.address}>
                        {messageContent.MESSAGE?.folderName}
                      </View>
                      {messageContent.MESSAGE?.projectName && (
                        <>
                          <View UNSAFE_className={styles.icon_chevron_right}>
                            {" "}
                            {">"}{" "}
                          </View>
                          <View UNSAFE_className={styles.address}>
                            {messageContent.MESSAGE?.projectName}
                          </View>
                        </>
                      )}
                      {messageContent.MESSAGE?.activityName && (
                        <>
                          <View UNSAFE_className={styles.icon_chevron_right}>
                            {" "}
                            {">"}{" "}
                          </View>
                          <View UNSAFE_className={styles.address}>
                            {messageContent.MESSAGE?.activityName}
                          </View>
                        </>
                      )}
                    </Flex>
                    <Flex
                      direction={"row"}
                      alignItems={"center"}
                      justifyContent={"center"}
                      gap={"6px"}
                    >
                      {messageContent?.participantsName && (
                        <View UNSAFE_className={styles.address}>
                          Participants: {messageContent.participantsName}
                        </View>
                      )}
                    </Flex>

                    <View
                      position={"relative"}
                      UNSAFE_className={styles.div_date}
                    >
                      {moment(selectedDataRow.date).format("DD.MM.YYYY HH:mm")}
                    </View>
                    <MessageAttachment
                      store={store}
                      styles={styles}
                      messageContent={messageContent}
                    />
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
            <View
              width={"93%"}
              position={"relative"}
              UNSAFE_className={styles.email_content}
              height={"55%"}
            >
              {/* <div
                dangerouslySetInnerHTML={{
                  __html: oldEmailContentFix(
                    messageContent.MESSAGE?.PREVIEW?.content
                  ),
                }}
              /> */}
              <iframe
                srcDoc={oldEmailContentFix(
                  messageContent.MESSAGE?.PREVIEW?.content,
                )}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                  backgroundColor: "#fff",
                }}
                title="Email Content"
                sandbox="allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox"
              />
            </View>
          </Flex>
        </Flex>
        <Flex
          direction={"column"}
          position={"absolute"}
          bottom={"55px"}
          left={"calc(50% - 128.5px)"}
          alignItems={"center"}
          justifyContent={"end"}
        >
          <Flex
            direction={"row"}
            width={"258px"}
            alignItems={"center"}
            justifyContent={"center"}
            gap={"32px"}
          >
            {navigationIndex > 0 && (
              <Flex
                direction={"row"}
                height={"32px"}
                alignItems={"center"}
                justifyContent={"center"}
                gap={"8px"}
                UNSAFE_className={styles.btn_previous}
              >
                <Button
                  variant="primary"
                  onPress={previousNavigation}
                  UNSAFE_style={{
                    background: "transparent",
                    border: "none",
                    width: "100%",
                  }}
                >
                  <>
                    <ArrowLeft className={styles.icon_navigate} />
                    &nbsp;&nbsp;&nbsp;
                    <View UNSAFE_className={styles.btn_text}>
                      {t("previous", { ns: "layout_components" })}
                    </View>
                  </>
                </Button>
              </Flex>
            )}
            {navigationIndex < rowsData.length - 1 && (
              <Flex
                direction={"row"}
                width={"113px"}
                height={"32px"}
                flexShrink={0}
                alignItems={"center"}
                justifyContent={"center"}
                gap={"8px"}
                UNSAFE_className={styles.btn_next}
              >
                <Button
                  variant="primary"
                  onPress={nextNavigation}
                  UNSAFE_style={{
                    background: "transparent",
                    border: "none",
                    width: "100%",
                  }}
                >
                  <>
                    <View UNSAFE_className={styles.btn_text}>
                      {t("next", { ns: "layout_components" })}
                    </View>
                    &nbsp;&nbsp;&nbsp;
                    <ArrowRight className={styles.icon_navigate} />
                  </>
                </Button>
              </Flex>
            )}
          </Flex>
        </Flex>
      </View>
    );
  };

  const renderContent = () => {
    if (!showDetail) {
      return renderMessageList();
    } else if (isMobile) {
      return renderMessageListDetailMobile();
    } else {
      return renderMessageListDetailLarge();
    }
  };

  const oldEmailContentFix = (emailContent: any) => {
    //remove padding: 16px; from the emailContent string
    const regex = /padding:\s*16px;/g;
    const fixedContent = emailContent?.replace(regex, "");

    return fixedContent;
  };

  if (showLoader) {
    return <Loader />;
  } else {
    return (
      <>
        {rowsData?.length > 0 ? (
          <div ref={componentRef}>
            {isMobile ||
            (componentRef.current && componentRef.current.offsetWidth < 780) ? (
              renderContent()
            ) : (
              <View
                width={"100%"}
                position={"relative"}
                height={"889px"}
                UNSAFE_className={styles.message_list_wrapper}
              >
                <View
                  position={"relative"}
                  top={0}
                  left={0}
                  width={"35%"}
                  height={"100%"}
                  overflow={"hidden"}
                  UNSAFE_className={styles.message_list_parent}
                >
                  <Flex
                    position={"relative"}
                    top={0}
                    left={0}
                    direction={"column"}
                    alignItems={"start"}
                    justifyContent={"start"}
                  >
                    <View
                      position={"relative"}
                      top={0}
                      left={0}
                      width={"100%"}
                      height={"64px"}
                      overflow={"hidden"}
                      UNSAFE_className={styles.messages_list_header_parent}
                    >
                      <Flex
                        direction={"row"}
                        position={"absolute"}
                        top={24}
                        height={"32px"}
                        alignItems={"center"}
                        justifyContent={"center"}
                        marginStart={12}
                        UNSAFE_className={styles.btn_new_email}
                      >
                        {/* {showNewMessageDialog && <NewMessage styles={styles} store={store} messageService={messageService} dialogVisibility={showNewMessageDialog} />} */}
                        <Button
                          variant="primary"
                          UNSAFE_style={{
                            background: "transparent",
                            border: "none",
                            width: "100%",
                            fontSize: "14px",
                          }}
                          onPress={() => {
                            //setShowNewMessageDialog(true);
                            invokeNewMessageCreating(
                              selectedItem?.id,
                              "message",
                              undefined,
                              undefined,
                              true,
                            );
                          }}
                        >
                          <>
                            <Plus
                              size={18}
                              className={styles.btn_new_email_text}
                            />
                            <span className={styles.btn_new_email_text}>
                              {t("new_email", { ns: "layout_components" })}
                            </span>
                          </>
                        </Button>
                      </Flex>
                      <Flex
                        position={"relative"}
                        marginTop={"25px"}
                        UNSAFE_style={{ float: "right" }}
                        direction={"row"}
                        alignItems={"center"}
                        justifyContent={"start"}
                        gap={15}
                      >
                        <ArrowClockwise
                          className={styles.message_list_header_icon}
                          onClick={reloadMessageList}
                        />
                        {/* <SearchIcon size={24} /> */}
                        <SearchButton />
                        {/* <FilterIcon UNSAFE_className={styles.message_list_header_icon} /> */}
                        <FilterButton />
                        <MoreIcon size={24} />
                      </Flex>
                    </View>
                    <Flex maxHeight="100vh" width={"100%"}>
                      <ListBox
                        autoFocus
                        id="listMessageCards"
                        selectionMode="single"
                        onSelectionChange={handleListRowClick}
                        defaultSelectedKeys={selectedKeys}
                        selectedKeys={selectedKeys}
                        aria-label="MessageListComponent"
                        items={listMessageList.items}
                        isLoading={listMessageList.isLoading}
                        onLoadMore={
                          totalMessageListRec > rowsData.length
                            ? listMessageList.loadMore
                            : undefined
                        }
                        width={"100%"}
                        UNSAFE_style={{ padding: "0 0 0 5px" }}
                      >
                        {(item) => (
                          <Item key={item.id}>
                            <CardComponent item={item} />
                          </Item>
                        )}
                      </ListBox>
                    </Flex>
                  </Flex>
                </View>
                <View
                  position={"absolute"}
                  top={0}
                  left={"35%"}
                  width={"1px"}
                  height={"100%"}
                  UNSAFE_className={styles.message_list_divider}
                ></View>
                {showLoaderDetail || listMessageList.isLoading ? (
                  <View
                    position={"absolute"}
                    top={15}
                    left={"36%"}
                    width={"65%"}
                    height={"98%"}
                    overflow={"hidden"}
                    UNSAFE_className={styles.message_list_detail_wrapper}
                  >
                    <Loader />
                  </View>
                ) : (
                  <div
                    role="main"
                    aria-label="Message detail"
                    className={styles.message_list_detail_wrapper}
                    style={{
                      position: "absolute",
                      top: 15,
                      left: "36%",
                      width: "64%",
                      height: "98%",
                      overflow: "hidden",
                    }}
                  >
                    {/* Structured, accessible message detail layout */}
                    <Flex
                      direction="column"
                      height="100%"
                      UNSAFE_className={styles.message_list_detail_parent}
                    >
                      {/* Header / Actions */}
                      <div
                        className={styles.message_list_detail_header_parent}
                        role="toolbar"
                        aria-label="Message actions"
                      >
                        <Flex
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          UNSAFE_style={{ padding: "10px 16px" }}
                        >
                          <Flex direction="row" alignItems="center" gap="12px">
                            {isMobile && (
                              <ActionButton
                                UNSAFE_className={styles.icon_back}
                                onPress={() => setShowDetail(false)}
                                aria-label="Back"
                              >
                                <ArrowLeft size={20} />
                              </ActionButton>
                            )}
                            <ActionButton
                              onPress={() => {
                                handleReplyClick("REPLY");
                              }}
                              UNSAFE_className={styles.action_button}
                              aria-label="Reply"
                            >
                              <ReplyIcon size={18} />
                            </ActionButton>

                            <ActionButton
                              onPress={() => {
                                handleReplyClick("REPLYALL");
                              }}
                              UNSAFE_className={styles.action_button}
                              aria-label="Reply all"
                            >
                              <ReplyAllIcon size={18} />
                            </ActionButton>
                            <ActionButton
                              UNSAFE_className={styles.action_button}
                              aria-label="Forward"
                            >
                              <ArrowForwardIcon size={18} />
                            </ActionButton>
                            <View
                              UNSAFE_style={{
                                width: "1px",
                                height: "24px",
                                background: "#eceff1",
                                marginLeft: 12,
                                marginRight: 12,
                                display: "none",
                              }}
                            >
                              <Button
                                variant="secondary"
                                UNSAFE_className={styles.btn_comment_text}
                                aria-label="Comment"
                              >
                                {t("comment", { ns: "layout_components" })}
                              </Button>
                            </View>
                          </Flex>

                          <Flex direction="row" alignItems="center" gap="10px">
                            <RemoveMessageButton />
                            <MoreIcon size={24} />
                          </Flex>
                        </Flex>
                      </div>

                      {/* Message metadata and subject */}
                      <View
                        UNSAFE_className={styles.email_content_header_parent}
                        UNSAFE_style={{
                          borderBottom: "1px solid #eceff1",
                          padding: "16px",
                        }}
                      >
                        <Flex direction="row" gap="18px" alignItems="start">
                          <div className={styles.user_img} aria-hidden>
                            <img
                              id="img_MessgeList"
                              className={styles.avatar_desktop_light}
                              alt=""
                              src="../../../../../../assets/images/avatar--desktop--light@2x.png"
                            />
                          </div>

                          <Flex direction="column" width="100%" gap="6px">
                            <Flex
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                            >
                              <Flex
                                direction="row"
                                alignItems="center"
                                gap="8px"
                              >
                                <View UNSAFE_className={styles.user_full_name}>
                                  {getSenderName(selectedDataRow.sender)}
                                </View>
                                <View
                                  UNSAFE_style={{
                                    width: "1px",
                                    height: "12px",
                                    background: "transparent",
                                  }}
                                />
                                <View UNSAFE_className={styles.user_email}>
                                  {getSenderEmail(selectedDataRow.mailFrom)}
                                </View>
                              </Flex>

                              <Text
                                UNSAFE_className={styles.div_date}
                                aria-hidden
                              >
                                {moment(selectedDataRow.date).format(
                                  "DD.MM.YYYY HH:mm",
                                )}
                              </Text>
                            </Flex>

                            <View
                              UNSAFE_className={styles.email_subject}
                              UNSAFE_style={{
                                fontSize: "18px",
                                fontWeight: 600,
                              }}
                            >
                              {selectedDataRow.subject}
                            </View>

                            <RecipientToField
                              recipient={selectedDataRow?.RECIPIENT}
                            />

                            <Flex
                              direction="row"
                              gap="8px"
                              alignItems="center"
                              UNSAFE_style={{
                                color: "#6f7e85",
                                fontSize: "13px",
                              }}
                            >
                              <View UNSAFE_className={styles.address}>
                                {messageContent.MESSAGE?.folderName}
                              </View>
                              {messageContent.MESSAGE?.projectName && (
                                <>
                                  <View
                                    UNSAFE_className={styles.icon_chevron_right}
                                  >
                                    {">"}
                                  </View>
                                  <View UNSAFE_className={styles.address}>
                                    {messageContent.MESSAGE?.projectName}
                                  </View>
                                </>
                              )}
                              {messageContent.MESSAGE?.activityName && (
                                <>
                                  <View
                                    UNSAFE_className={styles.icon_chevron_right}
                                  >
                                    {">"}
                                  </View>
                                  <View UNSAFE_className={styles.address}>
                                    {messageContent.MESSAGE?.activityName}
                                  </View>
                                </>
                              )}
                            </Flex>

                            {messageContent?.participantsName && (
                              <View
                                UNSAFE_style={{
                                  marginTop: "6px",
                                  color: "#6f7e85",
                                  fontSize: "13px",
                                }}
                              >
                                Participants: {messageContent.participantsName}
                              </View>
                            )}

                            <MessageAttachment
                              store={store}
                              styles={styles}
                              messageContent={messageContent}
                            />
                          </Flex>
                        </Flex>
                      </View>

                      {/* Message body */}
                      <View
                        UNSAFE_className={styles.email_content}
                        UNSAFE_style={{
                          padding: "16px",
                          overflow: "auto",
                          flex: 1,
                        }}
                      >
                        {/* <div
                          title={messageContent.MESSAGE?.PREVIEW?.contentv}
                          dangerouslySetInnerHTML={{
                            __html: oldEmailContentFix(
                              messageContent.MESSAGE?.PREVIEW?.content
                            ),
                          }}
                        /> */}

                        <iframe
                          srcDoc={oldEmailContentFix(
                            messageContent.MESSAGE?.PREVIEW?.content,
                          )}
                          style={{
                            width: "100%",
                            height: "100%",
                            border: "none",
                            backgroundColor: "#fff",
                          }}
                          title="Email Content"
                          sandbox="allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox"
                        />
                      </View>

                      {/* Footer navigation */}
                      <Flex
                        direction="row"
                        alignItems="center"
                        justifyContent="center"
                        UNSAFE_style={{ padding: "12px 0" }}
                      >
                        <Flex
                          direction="row"
                          gap="32px"
                          alignItems="center"
                          justifyContent="center"
                        >
                          {navigationIndex > 0 && (
                            <Button
                              variant="cta"
                              UNSAFE_style={{
                                background: "transparent",
                                border: "none",
                              }}
                              onPress={previousNavigation}
                              aria-label="Previous"
                            >
                              <ArrowLeft className={styles.icon_navigate} />
                              <span style={{ marginLeft: 8 }}>
                                {t("previous", { ns: "layout_components" })}
                              </span>
                            </Button>
                          )}

                          {navigationIndex < rowsData.length - 1 && (
                            <Button
                              variant="cta"
                              UNSAFE_style={{
                                background: "transparent",
                                border: "none",
                              }}
                              onPress={nextNavigation}
                              aria-label="Next"
                            >
                              <span style={{ marginRight: 8 }}>
                                {t("next", { ns: "layout_components" })}
                              </span>
                              <ArrowRight className={styles.icon_navigate} />
                            </Button>
                          )}
                        </Flex>
                      </Flex>
                    </Flex>
                  </div>
                )}
              </View>
            )}
          </div>
        ) : (
          <View
            position={"relative"}
            top={0}
            left={0}
            width={"100%"}
            height={"100%"}
            overflow={"hidden"}
            UNSAFE_className={styles.message_list_parent}
          >
            <Flex
              position={"relative"}
              top={0}
              left={0}
              direction={"column"}
              alignItems={"start"}
              justifyContent={"start"}
            >
              <View
                position={"relative"}
                top={0}
                left={0}
                width={"100%"}
                height={"64px"}
                overflow={"hidden"}
                UNSAFE_className={styles.messages_list_header_parent}
              >
                <Flex
                  direction={"row"}
                  position={"absolute"}
                  top={24}
                  height={"32px"}
                  alignItems={"center"}
                  justifyContent={"center"}
                  marginStart={12}
                  UNSAFE_className={styles.btn_new_email}
                >
                  {/* {showNewMessageDialog && <NewMessage styles={styles} store={store} messageService={messageService} dialogVisibility={showNewMessageDialog} />} */}
                  <Button
                    variant="primary"
                    UNSAFE_style={{
                      background: "transparent",
                      border: "none",
                      width: "100%",
                      fontSize: "14px",
                    }}
                    onPress={() => {
                      //setShowNewMessageDialog(true);
                      invokeNewMessageCreating(
                        selectedItem?.id,
                        "message",
                        undefined,
                        undefined,
                        true,
                      );
                    }}
                  >
                    <>
                      <Plus size={18} className={styles.btn_new_email_text} />
                      <span className={styles.btn_new_email_text}>
                        {t("new_email", { ns: "layout_components" })}
                      </span>
                    </>
                  </Button>
                </Flex>
                <Flex
                  position={"absolute"}
                  top={25}
                  right={15}
                  direction={"row"}
                  alignItems={"center"}
                  justifyContent={"start"}
                  gap={18}
                >
                  <ArrowClockwise
                    className={styles.message_list_header_icon}
                    onClick={reloadMessageList}
                  />
                  {/* <SearchIcon size={24} /> */}
                  <SearchButton />
                  {/* <FilterIcon UNSAFE_className={styles.message_list_header_icon} /> */}
                  <FilterButton />
                  <MoreIcon size={24} />
                </Flex>
              </View>
            </Flex>
            <NoResultsMessage />
          </View>
        )}
      </>
    );
  }
}

export const MessageListComponent = LayoutComponent(MessageList);
